/**
 * Dispatch board shapes — the supervisor's home screen.
 *
 * The board is a people × hour grid for a single day. The API returns
 * one shape with everything the screen needs:
 *
 *   - One row per active crew member (plus an Unassigned row at the bottom)
 *   - Each row carries that crew member's task assignments for the day
 *   - Tasks are denormalized into a `DispatchTask` shape (lean — just what
 *     the bar rendering needs)
 *   - A capacity summary for the header
 *
 * The board endpoint deliberately does NOT include attachments, notes, or
 * punch items — that's what GET /tasks/:id is for.
 */
import { z } from 'zod';
import { IsoDateTimeSchema, UuidSchema } from './common.js';
import { InstallSubtypeSchema, PickupStatusSchema, PickupSubtypeSchema, PrioritySchema, ProjectStatusSchema, TaskStatusSchema, TaskTypeSchema, } from './enums.js';
import { CrewRoleCountsSchema } from './tasks.js';
import { CrewMemberWithUserSchema } from './users.js';
/**
 * Lean task shape for the dispatch board. Includes the project title +
 * client_name denormalized so we don't need an extra fetch per task.
 */
export const DispatchTaskSchema = z.object({
    id: UuidSchema,
    project_id: UuidSchema,
    project_title: z.string(),
    project_client_name: z.string(),
    project_priority: PrioritySchema,
    type: TaskTypeSchema,
    pickup_subtype: PickupSubtypeSchema.nullable(),
    install_subtype: InstallSubtypeSchema.nullable(),
    sap_rma_reference: z.string().nullable(),
    title: z.string(),
    status: TaskStatusSchema,
    scheduled_start: IsoDateTimeSchema.nullable(),
    scheduled_end: IsoDateTimeSchema.nullable(),
    crew_size_needed: z.number().int().min(0),
    /** Planned per-role crew mix (from the studio); null for legacy/default tasks. */
    crew_roles_needed: CrewRoleCountsSchema.nullable(),
    truck_label: z.string().nullable(),
    /** Booked fleet vehicle (ADR-0019). */
    vehicle_id: UuidSchema.nullable(),
    vehicle_label: z.string().nullable(),
});
export const DispatchAssignmentSchema = z.object({
    task_id: UuidSchema,
    is_lead: z.boolean(),
    // Assignment block window (ADR-0013) — the crew member's own scheduled
    // truth, which the bar + capacity read. Falls back to the task window when
    // a block hasn't been scheduled yet.
    scheduled_start: IsoDateTimeSchema.nullable(),
    scheduled_end: IsoDateTimeSchema.nullable(),
    truck_label: z.string().nullable(),
});
/**
 * A PROJECT block the crew member holds on the day (D-3) — the project is the
 * schedulable unit, no task needed. Only blocks with a concrete window reach the
 * board (a crewless / unscheduled project stays in the needs-crew banner).
 */
export const DispatchProjectBlockSchema = z.object({
    project_id: UuidSchema,
    project_title: z.string(),
    client_name: z.string(),
    status: ProjectStatusSchema,
    is_lead: z.boolean(),
    scheduled_start: IsoDateTimeSchema,
    scheduled_end: IsoDateTimeSchema,
});
/** A PICKUP block the crew member holds on the day (workstream C). */
export const DispatchPickupBlockSchema = z.object({
    pickup_id: UuidSchema,
    title: z.string(),
    status: PickupStatusSchema,
    is_lead: z.boolean(),
    scheduled_start: IsoDateTimeSchema,
    scheduled_end: IsoDateTimeSchema,
    project_count: z.number().int().nonnegative(),
});
export const DispatchCrewRowSchema = z.object({
    crew_member: CrewMemberWithUserSchema,
    capacity_hours: z.number().min(0).max(24),
    assigned_hours: z.number().min(0),
    assignments: z.array(DispatchAssignmentSchema),
    /** Project blocks this crew member works directly that day (D-3). */
    project_blocks: z.array(DispatchProjectBlockSchema).default([]),
    /** Pickup blocks this crew member is on that day (workstream C). */
    pickup_blocks: z.array(DispatchPickupBlockSchema).default([]),
    /** True when two of this crew member's blocks overlap in time (ADR-0013). */
    has_conflict: z.boolean(),
});
export const DispatchCapacitySummarySchema = z.object({
    total_available_hours: z.number().min(0),
    total_assigned_hours: z.number().min(0),
    // Unbounded above: over-allocation is a real, displayed state (the board has a
    // ">100%" band), and assigning many crew to a full-day task can legitimately
    // push a single day well past 200% — never reject a busy day's response.
    capacity_used_pct: z.number().min(0),
    unassigned_count: z.number().int().min(0),
    blocked_count: z.number().int().min(0),
    /** Crew members whose blocks overlap themselves (double-booked). */
    conflict_count: z.number().int().min(0),
    /** Distinct vehicles booked on two overlapping tasks (ADR-0019). */
    vehicle_conflict_count: z.number().int().min(0),
});
export const DispatchDayResponseSchema = z.object({
    date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'must be YYYY-MM-DD'),
    capacity: DispatchCapacitySummarySchema,
    crew_rows: z.array(DispatchCrewRowSchema),
    unassigned_task_ids: z.array(UuidSchema),
    tasks_by_id: z.record(UuidSchema, DispatchTaskSchema),
    /** Task ids booked on a vehicle that's double-booked this day. */
    vehicle_conflict_task_ids: z.array(UuidSchema),
});
export const DispatchDayQuerySchema = z.object({
    date: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/, 'must be YYYY-MM-DD')
        .describe('Local date in YYYY-MM-DD'),
});
//# sourceMappingURL=dispatch.js.map